import React, { useCallback } from "react";
import type { Options } from "@ui/Highcharts";
import formatDate from "@ui/i18n/formatDate";
import formatNumber from "@ui/i18n/formatNumber";
import { renderToString } from "react-dom/server";
import { Text } from "@ui/Text";
import { colors } from "@ui/Theme";
import { css } from "@linaria/core";
import t from "../../../../../../i18n";
import { useTranslations } from "../../../../../../api/useTranslations";
import { translationLocale } from "@ui/i18n";

export interface FailureEvolutionHookOptions {
  minPeriod: number;
  maxPeriod: number;
  yAxisId: string;
  series: Highcharts.SeriesAreaOptions[];
  timeZone: string;
}

const useOptions = ({
  minPeriod,
  maxPeriod,
  yAxisId,
  series,
  timeZone,
}: FailureEvolutionHookOptions) => {
  const translations = useTranslations();
  const losses = translations[translationLocale.toUpperCase() as keyof typeof translations].LOSSES;
  const constantTooltipFormatter = useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      if (!this.point.y) return "";

      return renderToString(
        <div className={productionMonitoringTooltip}>
          <Text type="body2">{formatDate(this.point.x, "datetime", timeZone)}</Text>
          <Text type="body2SemiBold">
            {`${t("project.widget.average_losses.value", losses)} ${formatNumber(this.point.y, {
              sign: true,
              precision: 2,
              unit: "unit" in this.point.custom ? (this.point.custom.unit as string) : "",
            })}`}
          </Text>
        </div>,
      );
    },
    [losses, timeZone],
  );

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: {
          min: minPeriod,
          max: maxPeriod,
        },
      },
      chart: {
        type: "arearange",
        zoomType: undefined,
        marginTop: 5,
        marginRight: 5,
        marginLeft: 35,
      },
      series,
      xAxis: {
        type: "datetime",
        min: minPeriod,
        max: maxPeriod,
        tickPixelInterval: 150,
        labels: {
          style: {
            fontSize: "8px",
            fontWeight: "400",
            pointerEvents: "none",
            lineHeight: "12px",
          },
        },
      },
      yAxis: [
        {
          id: yAxisId,
          visible: true,
          tickAmount: 5,
          tickPixelInterval: 60,
          labels: {
            sign: false,
            enabled: true,
            unit: undefined,
            style: {
              fontSize: "8px",
              fontWeight: "400",
              pointerEvents: "none",
              lineHeight: "12px",
            },
          },
        },
      ],
      plotOptions: {
        area: {
          zIndex: 4000,
          marker: {
            enabled: true,
            radius: 2,
          },
        },
        series: {
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: false,
      },
    };
    return _;
  }, [series, yAxisId, minPeriod, maxPeriod, constantTooltipFormatter]);
};

const productionMonitoringTooltip = css`
  color: ${colors.carbon900};
`;

export default useOptions;
